// @ts-check
export const LegacyDrugCertificationsEnum = Object.freeze({
  "5 Panel No THC Drug Screen": "18",
  "5 Panel Drug Screen": "19",
  "6 Panel Drug Screen": "21",
  "10 Panel Drug Screen": "20",
});

export const DrugQualificationsEnum = Object.freeze({
  "5 Panel No THC Drug Screen": "aac0d6f9-dc3a-45d7-a288-b88a90883870",
  "5 Panel Drug Screen": "cf9e7af7-438a-4b1d-9c57-e4e0ad1c5505",
  "6 Panel Drug Screen": "20b1ca3a-5e71-4a45-b20e-578924f0fdc6",
  "10 Panel Drug Screen": "cc6fee57-4009-4629-b8fb-be629d43888b",
});

export const DrugQualificationsEnumReverse = Object.freeze({
  "aac0d6f9-dc3a-45d7-a288-b88a90883870": "5 Panel No THC Drug Screen",
  "cf9e7af7-438a-4b1d-9c57-e4e0ad1c5505": "5 Panel Drug Screen",
  "20b1ca3a-5e71-4a45-b20e-578924f0fdc6": "6 Panel Drug Screen",
  "cc6fee57-4009-4629-b8fb-be629d43888b": "10 Panel Drug Screen",
});

export const DrugQualificationsEnumToLegacy = Object.freeze({
  [DrugQualificationsEnum["5 Panel No THC Drug Screen"]]: LegacyDrugCertificationsEnum["5 Panel No THC Drug Screen"],
  [DrugQualificationsEnum["5 Panel Drug Screen"]]: LegacyDrugCertificationsEnum["5 Panel Drug Screen"],
  [DrugQualificationsEnum["10 Panel Drug Screen"]]: LegacyDrugCertificationsEnum["10 Panel Drug Screen"],
});

export const DRUG_SCREEN_VALUES = Object.freeze({
  NOT_ENABLED: 0,
  FIVE_PANEL_NO_THC: 1,
  FIVE_PANEL: 2,
  SIX_PANEL: 3,
  TEN_PANEL: 4,
});

// These should match the values defined in api-v2 (https://github.com/Bluecrew/api-v2/blob/develop/app/repositories/CompanyDataRepository.ts#L24)
export const BACKGROUND_CHECK_VALUES = Object.freeze({
  NOT_ENABLED: 0,
  BASIC_CRIMINAL_WITH_THREE_ALIASES: 1, // Level 2
  TASKER_PRO: 2, // Level 3
  ESSENTIAL_CRIMINAL_WITH_THREE_ALIASES: 3, // Level 4
  ESSENTIAL_CRIMINAL_WITH_ALIASES_TEN_YEAR: 4, // Level 5
  ESSENTIAL_CRIMINAL_WITH_THREE_ALIASES_SEVEN_YEAR: 5, // Level 6
  ESSENTIAL_CRIMINAL_WITH_UNLIMITED_ALIASES: 6, // Level 7
});

/**
 * Maps the qualificationId, which are pre-defined, of an Advanced Background Check qualification to the corresponding
 * Background Check value.
 */
export const BackgroundCheckToLevelEnum = Object.freeze({
  "50d7b749-5f48-4e0d-8089-6026ec7ad3dc": BACKGROUND_CHECK_VALUES.BASIC_CRIMINAL_WITH_THREE_ALIASES, // Level 2
  "109ca3c6-e815-4c52-9304-dd5b7465448c": BACKGROUND_CHECK_VALUES.TASKER_PRO, // Level 3
  "e61ad044-ba73-497f-b205-a4cf2ab3cd2d": BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_THREE_ALIASES, // Level 4
  "241d7b7a-aebc-4fb7-8224-13f999134a34": BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_ALIASES_TEN_YEAR, // Level 5
  "c83c823b-37af-489a-9809-5dcef68d885d": BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_THREE_ALIASES_SEVEN_YEAR, // Level 6
  "2be7e61c-f12b-4707-90e0-795c5542e243": BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_UNLIMITED_ALIASES, // Level 7
});

export const SCREENING = Object.freeze({
  DRUG: Object.freeze({
    VALUES: DRUG_SCREEN_VALUES,
    OPTIONS: Object.freeze({
      [DRUG_SCREEN_VALUES.NOT_ENABLED]: "Not enabled",
      [DRUG_SCREEN_VALUES.FIVE_PANEL_NO_THC]: "5 Panel no THC",
      [DRUG_SCREEN_VALUES.FIVE_PANEL]: "ecup 5 Panel",
      [DRUG_SCREEN_VALUES.TEN_PANEL]: "xCup 10 Panel",
    }),
    ID_TO_VALUE: Object.freeze({
      [DrugQualificationsEnum["5 Panel No THC Drug Screen"]]: DRUG_SCREEN_VALUES.FIVE_PANEL_NO_THC,
      [DrugQualificationsEnum["5 Panel Drug Screen"]]: DRUG_SCREEN_VALUES.FIVE_PANEL,
      [DrugQualificationsEnum["6 Panel Drug Screen"]]: DRUG_SCREEN_VALUES.SIX_PANEL,
      [DrugQualificationsEnum["10 Panel Drug Screen"]]: DRUG_SCREEN_VALUES.TEN_PANEL,
    }),
    /**
     * This is used to translate the drug certification types to their equivalent string values
     * to be used in the UI.
     */
    CERT_STRINGS: Object.freeze({
      tooltipMapping: Object.freeze({
        [DrugQualificationsEnum["5 Panel No THC Drug Screen"]]: "5-panel no THC",
        [DrugQualificationsEnum["5 Panel Drug Screen"]]: "5-panel",
        [DrugQualificationsEnum["6 Panel Drug Screen"]]: "6-panel",
        [DrugQualificationsEnum["10 Panel Drug Screen"]]: "10-panel",
      }),
    }),
  }),
  BACKGROUND: Object.freeze({
    VALUES: BACKGROUND_CHECK_VALUES,
    OPTIONS: Object.freeze({
      [BACKGROUND_CHECK_VALUES.NOT_ENABLED]: "Not enabled",
      [BACKGROUND_CHECK_VALUES.BASIC_CRIMINAL_WITH_THREE_ALIASES]: "Tasker Basic with Aliases",
      [BACKGROUND_CHECK_VALUES.TASKER_PRO]: "Tasker Pro",
      [BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_THREE_ALIASES]: "Tasker Pro with Aliases",
      [BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_ALIASES_TEN_YEAR]: "Essential Criminal with Aliases 10yr Lookback",
      [BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_THREE_ALIASES_SEVEN_YEAR]: "Essential Criminal plus Federal with 3 Aliases 7 Year Lookback",
      [BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_UNLIMITED_ALIASES]: "Essential Criminal with Unlimited Aliases plus Federal and State with 10yr Lookback",
    }),
    CERT_STRINGS: Object.freeze({
      tooltipMapping: Object.freeze({
        [BACKGROUND_CHECK_VALUES.BASIC_CRIMINAL_WITH_THREE_ALIASES]: "Level 2 indicates a background check with 3 aliases",
        [BACKGROUND_CHECK_VALUES.TASKER_PRO]: "Level 3 indicates a County criminal search covering the last 7 years",
        [BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_THREE_ALIASES]: "Level 4 indicates a background check with 3 aliases covering the last 7 years",
        [BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_ALIASES_TEN_YEAR]: "Level 5 indicates a County criminal search covering the last 10 years",
        [BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_THREE_ALIASES_SEVEN_YEAR]: "Level 6 indicates a County & Federal criminal search with 3 aliases covering the last 7 years",
        [BACKGROUND_CHECK_VALUES.ESSENTIAL_CRIMINAL_WITH_UNLIMITED_ALIASES]: "Level 7 indicates a County, Federal, & State criminal search with unlimited aliases covering the last 10 years",
      }),
    }),
  }),
});

// This contains qualifications that exist in Qualifications Service
export const QUALIFICATION = Object.freeze({
  TYPES: Object.freeze({
    BACKGROUND_CHECK: "BACKGROUND_CHECK",
    DRUG_SCREEN: "DRUG_SCREEN",
  }),
  ICON_URLS: Object.freeze({
    BACKGROUND_CHECK: "images/cert-icons/background-check.svg",
    DRUG_SCREEN: "images/cert-icons/drug-screen.svg",
  }),
});
